import React, { useMemo, useRef } from "react";
import NiceModal, { NiceModalHocProps, useModal } from "@ebay/nice-modal-react";
import { Config, Prize, StockStrategy } from "kadow-jetpack-model";
import { Formik, type FormikErrors, FormikTouched, Form } from "formik";
import * as Yup from "yup";
import GiftForm from "./GiftForm";
import KC_Modal from "../../../../designsystem/KC_Modal/KC_Modal";
import KC_Button from "../../../../designsystem/KC_Button/KC_Button";
import { tt } from "../../../../utils/i18n/i18n";
import { giftNameValidation } from "../../../19_JetpackConfig/components/GiftsConfiguration/helper/prize_validation_schema";
import { FIELD_REQUIRED, PERCENTAGE } from "../../../../utils/constants";
import { buildBaseGift } from "../../../../utils/helpers/buildBaseGift";
import KC_Separator from "../../../../designsystem/KC_Separator/KC_Separator";
import { KC_Sizes } from "../../../../designsystem/ds_commons";
import ManagedAccount from "@tootsweet/model/entities/ManagedAccount";

type FormType = {
  prizes: Prize[];
};

interface Props {
  initialPrize?: Prize;
  hasAdvancedCouponOptions: boolean;
  errors: FormikErrors<Config>;
  touched?: FormikTouched<Config>;
  accountToDisplay: ManagedAccount;
  removeItem?: () => void;
}

const prizeValidationSchema = Yup.object().shape({
  name: giftNameValidation,
  image: Yup.string().required(FIELD_REQUIRED),
  pc: Yup.number()
    .transform((value, originalValue) => {
      if (
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return undefined;
      }
      return isNaN(value) ? undefined : value;
    })
    .min(0, PERCENTAGE)
    .max(100, PERCENTAGE)
    .required(FIELD_REQUIRED),
  iQty: Yup.number().when("ss", {
    is: (ss: string) => ss !== StockStrategy.None,
    then: (schema) =>
      schema
        .transform((value, originalValue) => {
          if (
            originalValue === "" ||
            originalValue === null ||
            originalValue === undefined
          ) {
            return undefined;
          }
          return isNaN(value) ? undefined : value;
        })
        .min(1, FIELD_REQUIRED)
        .required(FIELD_REQUIRED)
  })
});

const EditGiftModal = NiceModal.create<Props & NiceModalHocProps>((props) => {
  const modal = useModal();

  const getValuesRef = useRef<Prize | null>(null);

  const handleClose = () => {
    modal.resolve(null);
    void modal.hide();
  };

  const onSubmit = async (values: FormType) => {
    modal.resolve(values.prizes[0]);
    void modal.hide();
  };

  const initialValues = useMemo(() => {
    if (props.initialPrize) {
      return {
        prizes: [props.initialPrize]
      };
    }
    return {
      prizes: [buildBaseGift(props.accountToDisplay)]
    };
  }, [props.accountToDisplay, props.initialPrize]);

  return (
    <KC_Modal isOpen={modal.visible} onClose={handleClose}>
      <Formik<FormType>
        initialValues={initialValues}
        validationSchema={Yup.object({
          prizes: Yup.array().of(prizeValidationSchema)
        })}
        onSubmit={onSubmit}
        enableReinitialize
        validateOnBlur={true}
      >
        {({ values, setFieldValue, setFieldTouched, errors, touched }) => {
          if (values.prizes?.length) {
            getValuesRef.current = values.prizes[0];
          }

          return (
            <Form>
              <GiftForm
                hideRemove
                hideGenerateIdea={true}
                // @ts-ignore
                config={values}
                prizeIndex={0}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                hasAdvancedCouponOptions={props.hasAdvancedCouponOptions}
                removeItem={() => {
                  props.removeItem?.();
                  handleClose();
                }}
                errors={errors}
                touched={touched}
                onClickGenerateIdea={handleClose}
              />
              <KC_Separator size={KC_Sizes.m} />
              <div className="w-100">
                <KC_Button
                  text={tt("save")}
                  extraStyle={{ width: "auto" }}
                  type="submit"
                  className="ml-auto"
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </KC_Modal>
  );
});

export default EditGiftModal;
